import { ChangeDetectionStrategy, Component, effect, inject, input, output } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { debounceTime, filter, map } from 'rxjs/operators';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';

import { GenerationChatComponent } from '../generation-chat/generation-chat.component';
import { GenerationChatService } from '../generation-chat/generation-chat.service';
import { AiAssistantI18nModule } from '../../assets/i18n/ai-assistant-i18n.module';

const AUTOMATION_GENERATION_TYPE = 'automation';

/**
 * No-op signal the assistant emits after every mutating edit. It performs no write itself; it tells
 * the builder to refresh the tree and which step to focus.
 */
const REFRESH_AUTOMATION_TOOL = 'RefreshAutomation';

interface RefreshAutomationArgs {
  stepToFocus?: string;
}

@Component({
  selector: 'ai-automation-assistant-builder',
  imports: [MatButtonModule, MatIconModule, GenerationChatComponent, AiAssistantI18nModule],
  providers: [GenerationChatService],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './automation-assistant-builder.component.html',
  styleUrl: './automation-assistant-builder.component.scss',
})
export class AutomationAssistantBuilderComponent {
  readonly automationId = input<string | null>(null);

  readonly cancelled = output<void>();
  /** Emits the step id to focus after the change (null = focus the trigger). */
  readonly automationChanged = output<string | null>();

  private readonly chatService = inject(GenerationChatService);

  constructor() {
    this.chatService.setGenerationType(AUTOMATION_GENERATION_TYPE);
    this.chatService.setAssistantId('ASSISTANT-9b4fd96e-0d6d-401f-97e1-afd71f47cdc3');

    effect(() => {
      const id = this.automationId();
      this.chatService.setExtraContextVariables(id ? [{ key: 'AutomationID', value: id }] : []);
    });

    this.chatService.functionComplete$
      .pipe(
        filter((event) => event.functionName === REFRESH_AUTOMATION_TOOL),
        map((event) => (event.parsedArgs as RefreshAutomationArgs | null)?.stepToFocus ?? null),
        debounceTime(0),
        takeUntilDestroyed(),
      )
      .subscribe((focusStepId) => this.automationChanged.emit(focusStepId));
  }

  cancel(): void {
    this.cancelled.emit();
  }
}
